import { type ReactNode, useEffect } from "react";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeContext";
import { useLocation } from "wouter";
import { UnifiedSidebar } from "@/components/sidebar";
import { GlobalHeader } from "@/components/GlobalHeader";
import { MobileTabBar } from "@/components/MobileTabBar";
import { UserMenu } from "@/components/sidebar/UserMenu";


interface MainLayoutProps {
  children: ReactNode;
}

/**
 * MainLayout provides the shell for authenticated pages.
 * Uses UnifiedSidebar which automatically detects admin vs main routes.
 *
 * T-383 & T-385: The sidebar now handles both main app and admin navigation,
 * with a consistent user menu showing avatar, name, role, and usage counter.
 */
export default function MainLayout({ children }: MainLayoutProps) {
  const { isAuthenticated, isLoading } = useAuth();
  const { favicon } = useTheme();
  const [, navigate] = useLocation();
  const [location] = useLocation();

  // Handle favicon updates
  useEffect(() => {
    if (favicon) {
      const faviconElement = document.querySelector("link[rel='icon']") as HTMLLinkElement;
      if (faviconElement) {
        faviconElement.href = favicon;
      } else {
        const newFavicon = document.createElement('link');
        newFavicon.rel = 'icon';
        newFavicon.href = favicon;
        document.head.appendChild(newFavicon);
      }
    }
  }, [favicon]);

  const publicRoutes = ['/login', '/register', '/waiting-list', '/'];
  const authRoutes = ['/login', '/register'];
  const isPublicRoute = publicRoutes.includes(location);
  const isAuthRoute = authRoutes.includes(location);

  // Redirect unauthenticated users
  if (!isAuthenticated && !isPublicRoute) {
    if (isLoading) {
      return <div>Loading...</div>;
    }
    navigate('/login');
    return null;
  }

  // Public routes without sidebar (unauthenticated)
  if (!isAuthenticated && isPublicRoute) {
    return <div className="min-h-screen bg-gray-50 dark:bg-[#101415]">{children}</div>;
  }

  // Auth routes (login, register) should never show sidebar, even when authenticated
  // This handles the edge case where an authenticated user navigates to /login
  if (isAuthRoute) {
    return <div className="min-h-screen bg-gray-50 dark:bg-[#101415]">{children}</div>;
  }

  return (
    <div className="flex h-screen flex-col bg-gray-50 dark:bg-[#101415] overflow-hidden">
      <GlobalHeader />
      {/* AR-316 — below md the rail leaves the flow and navigation becomes the
          v9 bottom tab bar. The row is sized by flex, not by a vh calc, so the
          bar simply takes 56px off it. */}
      <div className="flex flex-1 min-h-0">
        <div className="hidden md:flex">
          <UnifiedSidebar />
        </div>
        <main className="flex-1 overflow-auto bg-white dark:bg-[#101415]">
          {children}
        </main>
      </div>
      <MobileTabBar footer={<UserMenu expanded inline />} />
    </div>
  );
}
